import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { t } from '../lib/i18n';
import './LoginPage.css';

function KakaoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 2C5.582 2 2 4.79 2 8.222c0 2.178 1.417 4.09 3.567 5.188l-.91 3.39a.25.25 0 0 0 .378.274L9.1 14.678c.296.028.597.044.9.044 4.418 0 8-2.79 8-6.222C18 4.79 14.418 2 10 2z"
        fill="#3C1E1E"
      />
    </svg>
  );
}

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleKakaoLogin = async () => {
    setError('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'kakao',
      options: {
        redirectTo: 'https://lifelog.mobile',
      },
    });
    if (error) setError(error.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    if (mode === 'reset') {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://lifelog.mobile',
      });
      if (error) {
        setError(error.message);
      } else {
        setMessage('비밀번호 재설정 이메일을 보냈습니다. 이메일을 확인해주세요.');
      }
      setLoading(false);
      return;
    }

    if (mode === 'login') {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setError(error.message);
    } else {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) {
        setError(error.message);
      } else {
        setMessage('가입 확인 이메일을 보냈습니다. 이메일을 확인해주세요.');
      }
    }

    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-logo-circle">
          <span className="login-logo">📖</span>
        </div>
        <h1 className="login-title">{t('login_title')}</h1>
        <p className="login-subtitle">{t('login_subtitle')}</p>
      </div>

      <div className="login-card">
      <form className="login-form" onSubmit={handleSubmit}>
        {error && <p className="login-error">{error}</p>}
        {message && <p className="login-message">{message}</p>}

        <div className="form-group">
          <label htmlFor="email">{t('login_email')}</label>
          <input
            id="email"
            type="email"
            placeholder="example@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>

        {mode !== 'reset' && (
          <div className="form-group">
            <label htmlFor="password">{t('login_password')}</label>
            <input
              id="password"
              type="password"
              placeholder="6자 이상"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </div>
        )}

        <button className="btn-primary" type="submit" disabled={loading}>
          {loading
            ? t('login_processing')
            : mode === 'reset'
              ? t('login_reset_email')
              : mode === 'login'
                ? t('login_btn')
                : t('login_signup')}
        </button>
      </form>

      {mode !== 'reset' && (
        <>
          <div className="login-divider">
            <span>{t('login_or')}</span>
          </div>

          <button className="btn-kakao" type="button" onClick={handleKakaoLogin}>
            <KakaoIcon />
            {t('login_kakao')}
          </button>
        </>
      )}

      <div className="login-footer">
        {mode === 'login' && (
          <>
            <p>
              {t('login_forgot')}{' '}
              <button type="button" className="btn-link" onClick={() => { setMode('reset'); setError(''); setMessage(''); }}>
                {t('login_find_pw')}
              </button>
            </p>
            <p>
              {t('login_no_account')}{' '}
              <button type="button" className="btn-link" onClick={() => { setMode('signup'); setError(''); setMessage(''); }}>
                {t('login_signup')}
              </button>
            </p>
          </>
        )}
        {mode === 'signup' && (
          <p>
            {t('login_has_account')}{' '}
            <button type="button" className="btn-link" onClick={() => { setMode('login'); setError(''); setMessage(''); }}>
              {t('login_btn')}
            </button>
          </p>
        )}
        {mode === 'reset' && (
          <p>
            <button type="button" className="btn-link" onClick={() => { setMode('login'); setError(''); setMessage(''); }}>
              {t('login_back_to_login')}
            </button>
          </p>
        )}
      </div>
      </div>{/* login-card end */}

      <section className="login-seo-features">
        <h2>인생의 모든 순간을 타임라인으로 기록하세요</h2>
        <ul>
          <li><strong>육아일기</strong> — 아이의 성장을 날마다 사진과 함께 기록</li>
          <li><strong>성장기록</strong> — 가족 구성원별 프로필로 각자의 타임라인 관리</li>
          <li><strong>여행기록</strong> — 여행일기를 지도와 사진으로 생생하게 저장</li>
          <li><strong>인생 타임라인</strong> — 나의 인생 전체를 한눈에 돌아보기</li>
          <li><strong>추억 공유</strong> — 소중한 기록을 가족과 함께 공유</li>
          <li><strong>PDF 출력</strong> — 기록을 책처럼 뽑아 영원히 보관</li>
        </ul>
        <p>육아일기 어플, 성장일기, 여행일기 어플, 가족앱, 기록앱, 인생로그, 인생타임라인앱 — 무료로 시작하세요.</p>
      </section>
    </div>
  );
}
